import { useEffect, useRef, useState } from 'react';
import type { ApiClient } from '../api/client';
import { EventStream, type EventStreamState } from '../api/events';
import type { StudioEvent } from '../api/types';

export interface StudioEventsOptions {
  api: ApiClient;
  workspaceId: string;
  /** snapshot（一覧など）の eventCursor。null なら接続時点以後だけを受け取る。 */
  cursor: string | null;
  enabled: boolean;
  /** サーバーで絞る（C26）。Studio 画面は Workspace 全体を 1 本で購読するので渡さない。 */
  sessionId?: string;
  appId?: string;
  onEvent(event: StudioEvent): void;
}

/**
 * W/studio/events の購読。再接続・重複排除・410 は EventStream が platform の購読と同じに扱う。
 * expired を返したら呼び出し側が snapshot を取り直す。
 */
export function useStudioEvents(options: StudioEventsOptions): EventStreamState {
  const [state, setState] = useState<EventStreamState>('closed');
  const onEvent = useRef(options.onEvent);
  onEvent.current = options.onEvent;
  const { api, workspaceId, cursor, enabled, sessionId, appId } = options;

  useEffect(() => {
    if (!enabled) return;
    const stream = new EventStream<StudioEvent>(
      api,
      {
        workspaceId,
        filter: {},
        ...(cursor ? { cursor } : {}),
        open: (current, signal) =>
          api.openStudioEvents(
            workspaceId,
            {
              ...(current ? { cursor: current } : {}),
              ...(sessionId ? { sessionId } : {}),
              ...(appId ? { appId } : {}),
            },
            signal,
          ),
      },
      { onEvent: (event) => onEvent.current(event), onState: setState },
    );
    void stream.start();
    return () => stream.close();
  }, [api, workspaceId, cursor, enabled, sessionId, appId]);

  return state;
}
